import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocale } from '../../localization/LocaleContext';

const SLIDE_INTERVAL = 5000; // 5 seconds auto-advance

export default function InvoicePointVision() {
    const sectionId = 'ai-estimator';
    const { t } = useLocale();
    const [email, setEmail] = useState('');
    const [error, setError] = useState('');
    const [submitted, setSubmitted] = useState(false);
    const [loading, setLoading] = useState(false);
    const [activeSlide, setActiveSlide] = useState(0);
    const timerRef = useRef(null);

    const slides = [
        {
            id: 'bathroom',
            image: '/images/bathroom-renovation.jpg',
            imagePosition: 'center 40%',
            photoAlt: t('vision.photoAlt'),
            detections: [
                { boxClass: 'vision-box-tiles', labelClass: 'vision-label-tiles', label: t('vision.labelTiles') },
                { boxClass: 'vision-box-bathtub', labelClass: 'vision-label-bathtub', label: t('vision.labelBathtub') },
            ],
            estimate: {
                row1Label: t('vision.labor'),
                row1Sub: t('vision.laborEst'),
                row1Value: t('vision.laborPrice'),
                row2Label: t('vision.materials'),
                row2Value: t('vision.materialsPrice'),
                total: t('vision.totalPrice'),
            },
        },
        {
            id: 'garden',
            image: '/images/garden-paving.jpg',
            imagePosition: 'center center',
            photoAlt: t('vision.gardenPhotoAlt'),
            detections: [
                { boxClass: 'vision-box-driveway', labelClass: 'vision-label-driveway', label: t('vision.labelDriveway') },
                { boxClass: 'vision-box-lawn', labelClass: 'vision-label-lawn', label: t('vision.labelLawn') },
            ],
            estimate: {
                row1Label: t('vision.labor'),
                row1Sub: t('vision.gardenLaborEst'),
                row1Value: t('vision.gardenLaborPrice'),
                row2Label: t('vision.materials'),
                row2Value: t('vision.gardenMaterialsPrice'),
                total: t('vision.gardenTotalPrice'),
            },
        },
        {
            id: 'kitchen',
            image: '/images/kitchen-sink.jpg',
            imagePosition: 'center center',
            photoAlt: t('vision.kitchenPhotoAlt'),
            detections: [
                { boxClass: 'vision-box-sink', labelClass: 'vision-label-sink', label: t('vision.labelSink') },
                { boxClass: 'vision-box-plumbing', labelClass: 'vision-label-plumbing', label: t('vision.labelPlumbing') },
            ],
            estimate: {
                row1Label: t('vision.labor'),
                row1Sub: t('vision.kitchenLaborEst'),
                row1Value: t('vision.kitchenLaborPrice'),
                row2Label: t('vision.materials'),
                row2Value: t('vision.kitchenMaterialsPrice'),
                total: t('vision.kitchenTotalPrice'),
            },
        },
        {
            id: 'paint',
            image: '/images/room-paint.jpg',
            imagePosition: 'center center',
            photoAlt: t('vision.paintPhotoAlt'),
            detections: [
                { boxClass: 'vision-box-walls', labelClass: 'vision-label-walls', label: t('vision.labelWalls') },
                { boxClass: 'vision-box-ceiling', labelClass: 'vision-label-ceiling', label: t('vision.labelCeiling') },
            ],
            estimate: {
                row1Label: t('vision.labor'),
                row1Sub: t('vision.paintLaborEst'),
                row1Value: t('vision.paintLaborPrice'),
                row2Label: t('vision.materials'),
                row2Value: t('vision.paintMaterialsPrice'),
                total: t('vision.paintTotalPrice'),
            },
        },
    ];

    const goToSlide = useCallback((index) => {
        setActiveSlide(index);
    }, []);

    const nextSlide = useCallback(() => {
        setActiveSlide(prev => (prev + 1) % slides.length);
    }, [slides.length]);

    const prevSlide = useCallback(() => {
        setActiveSlide(prev => (prev - 1 + slides.length) % slides.length);
    }, [slides.length]);

    // Auto-advance timer
    useEffect(() => {
        timerRef.current = setInterval(nextSlide, SLIDE_INTERVAL);
        return () => clearInterval(timerRef.current);
    }, [nextSlide]);

    // Reset timer on manual navigation
    function resetTimer() {
        clearInterval(timerRef.current);
        timerRef.current = setInterval(nextSlide, SLIDE_INTERVAL);
    }

    function handlePrev() { prevSlide(); resetTimer(); }
    function handleNext() { nextSlide(); resetTimer(); }
    function handleDot(i) { goToSlide(i); resetTimer(); }

    const currentSlide = slides[activeSlide];

    function isValidEmail(value) {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
    }

    function handleSubmit() {
        const trimmed = email.trim();
        if (!isValidEmail(trimmed)) {
            setError(t('vision.emailError'));
            return;
        }
        setError('');
        setLoading(true);
        fetch(
            `https://core.invoicepoint.app/en/waitinglist?email=${encodeURIComponent(trimmed)}`,
            { mode: 'no-cors' }
        ).finally(() => {
            setLoading(false);
            setSubmitted(true);
        });
    }

    const features = [
        {
            id: 1,
            icon: (
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
                    <circle cx="12" cy="13" r="4"/>
                </svg>
            ),
            heading: t('vision.feature1Heading'),
            description: t('vision.feature1Description'),
        },
        {
            id: 2,
            icon: (
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"/>
                    <line x1="22" y1="12" x2="18" y2="12"/>
                    <line x1="6" y1="12" x2="2" y2="12"/>
                    <line x1="12" y1="6" x2="12" y2="2"/>
                    <line x1="12" y1="22" x2="12" y2="18"/>
                </svg>
            ),
            heading: t('vision.feature2Heading'),
            description: t('vision.feature2Description'),
        },
        {
            id: 3,
            icon: (
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M15 12l-8.5 8.5a2.12 2.12 0 0 1-3-3L12 9"/>
                    <path d="M17.64 15L22 10.64"/>
                    <path d="M20.91 11.7l-1.25-1.25c-.6-.6-.93-1.4-.93-2.25v-.86L16.01 4.6a5.56 5.56 0 0 0-3.94-1.64H9l.92.82A6.18 6.18 0 0 1 12 8.4v1.56l2 2h2.47l2.26 1.91"/>
                </svg>
            ),
            heading: t('vision.feature3Heading'),
            description: t('vision.feature3Description'),
        },
        {
            id: 4,
            icon: (
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                    <polyline points="14 2 14 8 20 8"/>
                    <line x1="9" y1="15" x2="15" y2="15"/>
                    <polyline points="12 12 15 15 12 18"/>
                </svg>
            ),
            heading: t('vision.feature4Heading'),
            description: t('vision.feature4Description'),
        },
    ];

    return (
        <section id={sectionId} className="vision-section sp-90">
            <div className="container">
                <div className="row align-items-center">

                    {/* ── Left column ── */}
                    <div className="col-lg-6 mb-4 mb-lg-0">
                        <div className="vision-product-badge">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                                {/* Large sparkle */}
                                <path d="M8 1 C8.5 4.5 11.5 7.5 16 9 C11.5 10.5 8.5 13.5 8 17 C7.5 13.5 4.5 10.5 0 9 C4.5 7.5 7.5 4.5 8 1 Z"/>
                                {/* Small sparkle */}
                                <path d="M18 13 C18.25 14.5 19.75 16.5 22 17 C19.75 17.5 18.25 19.5 18 21 C17.75 19.5 16.25 17.5 14 17 C16.25 16.5 17.75 14.5 18 13 Z"/>
                            </svg>
                            {t('vision.badge')}
                        </div>

                        <h2 className="h-1 h-bold vision-heading">
                            {t('vision.heading')}
                        </h2>

                        <p className="paragraph p-adjustment vision-desc">
                            {t('vision.description')}
                        </p>

                        <div className="vision-features-grid">
                            {features.map((f) => (
                                <div key={f.id} className="vision-feature-card">
                                    <div className="vision-feature-icon">{f.icon}</div>
                                    <h5 className="h-5 bold-500 mt-10 vision-feature-heading">{f.heading}</h5>
                                    <p className="paragraph p-adjustment mb-0">{f.description}</p>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* ── Right column – dark mockup ── */}
                    <div className="col-lg-6">
                        <div className="vision-mockup">

                            <div className="vision-ai-badge">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                                    {/* Large sparkle */}
                                    <path d="M8 1 C8.5 4.5 11.5 7.5 16 9 C11.5 10.5 8.5 13.5 8 17 C7.5 13.5 4.5 10.5 0 9 C4.5 7.5 7.5 4.5 8 1 Z"/>
                                    {/* Small sparkle */}
                                    <path d="M18 13 C18.25 14.5 19.75 16.5 22 17 C19.75 17.5 18.25 19.5 18 21 C17.75 19.5 16.25 17.5 14 17 C16.25 16.5 17.75 14.5 18 13 Z"/>
                                </svg>
                                {t('vision.aiBadge')}
                            </div>

                            <h3 className="vision-mockup-title">{t('vision.mockupTitle')} {t('vision.mockupComingSoon')}</h3>
                            <p className="vision-mockup-subtitle">
                                {t('vision.mockupSubtitle')}
                            </p>

                            {/* Photo carousel — estimate card overlaid at the bottom */}
                            <div className="vision-photo-wrapper">
                                {/* Left arrow */}
                                <button className="vision-carousel-arrow vision-arrow-left" onClick={handlePrev} aria-label="Previous slide">
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                        <polyline points="15 18 9 12 15 6" />
                                    </svg>
                                </button>
                                {/* Right arrow */}
                                <button className="vision-carousel-arrow vision-arrow-right" onClick={handleNext} aria-label="Next slide">
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                        <polyline points="9 6 15 12 9 18" />
                                    </svg>
                                </button>

                                <img
                                    key={currentSlide.id}
                                    src={currentSlide.image}
                                    alt={currentSlide.photoAlt}
                                    className="vision-photo-img vision-photo-fade"
                                    style={{ objectPosition: currentSlide.imagePosition }}
                                />
                                <div className="vision-scan-line" key={`scan-${currentSlide.id}`} />

                                {currentSlide.detections.map((det, i) => (
                                    <React.Fragment key={`${currentSlide.id}-det-${i}`}>
                                        <div className={`vision-selection-box ${det.boxClass}`} />
                                        <div className={`vision-detect-label ${det.labelClass}`}>{det.label}</div>
                                    </React.Fragment>
                                ))}

                            {/* Carousel dots */}
                            <div className="vision-carousel-dots">
                                {slides.map((s, i) => (
                                    <button
                                        key={s.id}
                                        className={`vision-carousel-dot${i === activeSlide ? ' active' : ''}`}
                                        onClick={() => handleDot(i)}
                                        aria-label={`Go to slide ${i + 1}`}
                                    />
                                ))}
                            </div>
                            </div>{/* end vision-photo-wrapper */}

                            {/* Estimate result card — slightly overlaps photo bottom */}
                            <div className="vision-estimate-card">
                                <div className="vision-estimate-header">
                                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                                        <polyline points="14 2 14 8 20 8"/>
                                        <line x1="16" y1="13" x2="8" y2="13"/>
                                        <line x1="16" y1="17" x2="8" y2="17"/>
                                        <line x1="10" y1="9" x2="8" y2="9"/>
                                    </svg>
                                    <span>{t('vision.estimateGenerated')}</span>
                                </div>

                                <div className="vision-estimate-row">
                                    <span className="vision-estimate-label">
                                        {currentSlide.estimate.row1Label} <span className="vision-estimate-sub">{currentSlide.estimate.row1Sub}</span>
                                    </span>
                                    <span className="vision-estimate-value">{currentSlide.estimate.row1Value}</span>
                                </div>

                                <div className="vision-estimate-row">
                                    <span className="vision-estimate-label">{currentSlide.estimate.row2Label}</span>
                                    <span className="vision-estimate-value">{currentSlide.estimate.row2Value}</span>
                                </div>

                                <div className="vision-estimate-divider" />

                                <div className="vision-estimate-row">
                                    <span className="vision-estimate-total-label">{t('vision.totalEstimate')}</span>
                                    <span className="vision-estimate-total-value">{currentSlide.estimate.total}</span>
                                </div>
                            </div>

                            {/* Waitlist form */}
                            {submitted ? (
                                <div className="vision-thankyou">
                                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                        <polyline points="20 6 9 17 4 12" />
                                    </svg>
                                    <div>
                                        <p className="vision-thankyou-title">{t('vision.thankYouTitle')}</p>
                                        <p className="vision-thankyou-msg">{t('vision.thankYouMessage', { email: email.trim() })}</p>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <p className="vision-waitlist-text">{t('vision.waitlistText')}</p>
                                    <div className="vision-notify-form">
                                        <input
                                            type="email"
                                            placeholder={t('vision.emailPlaceholder')}
                                            className={`vision-email-input${error ? ' vision-email-input--error' : ''}`}
                                            aria-label={t('vision.emailPlaceholder')}
                                            value={email}
                                            onChange={(e) => { setEmail(e.target.value); setError(''); }}
                                            onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
                                        />
                                        <button className="vision-notify-btn" type="button" onClick={handleSubmit} disabled={loading}>
                                            {loading ? '...' : t('vision.notifyBtn')}
                                        </button>
                                    </div>
                                    {error && <p className="vision-email-error">{error}</p>}
                                </>
                            )}

                        </div>
                    </div>

                </div>
            </div>
        </section>
    );
}
